import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { useApiQuery, useTypedQueryParams } from "../hooks";
import type { ApiConfig } from "../types";

type APIKeyContextType = {
  apiKey: string;
  setApiKey: (key: string) => void;
  hasKey: boolean;
};

export const APIKeyContext = createContext<APIKeyContextType>({
  apiKey: "",
  setApiKey: () => {},
  hasKey: false,
});

export const useAPIKeyContext = () => {
  return useContext(APIKeyContext) as APIKeyContextType;
};

export const APIKeyProvider = ({
  children,
  loggedIn,
}: {
  children: ReactNode;
  loggedIn: boolean;
}) => {
  const [apiKey, setApiKey] = useState<string>("");
  const { params, setParam } = useTypedQueryParams<{ apiKey?: string }>();

  const { data: config } = useApiQuery<ApiConfig>("/config", {
    enabled: loggedIn,
  });

  useEffect(() => {
    if (loggedIn && config?.api_keys.length !== 0) {
      const maybeKey = config?.api_keys[0]?.key;

      if (maybeKey && maybeKey.length > 0) {
        setApiKey(maybeKey);
      }
    }
  }, [loggedIn, config?.api_keys]);

  useEffect(() => {
    if (!!params.apiKey && params.apiKey.length > 0) {
      setApiKey(params.apiKey);
      setParam("apiKey", undefined);
    }
  }, [params.apiKey, setParam]);

  return (
    <APIKeyContext.Provider
      value={{
        apiKey,
        setApiKey,
        hasKey: apiKey.length > 0,
      }}
    >
      {children}
    </APIKeyContext.Provider>
  );
};
